/**
 * Génération des créneaux de retrait/livraison (étape 2 — parité legacy).
 *
 * Port fidèle de la logique legacy (`genTimeSlots` / `hoursArrayToMap` /
 * `restoMapToDisplay`, cf. apprentissages M10 & M14) en fonctions PURES et
 * testables. Les créneaux sont générés depuis `tenants.opening_hours` — jamais
 * un champ texte libre, jamais une liste figée.
 *
 * Règles couvertes :
 *  - pas (step) configurable, défaut 30 min ;
 *  - délai de préparation avant le 1ᵉʳ créneau du jour ;
 *  - fermeture après minuit (close ≤ open ⇒ fin le lendemain) → « jusqu'à 02:00 » OK ;
 *  - fermetures exceptionnelles (`closures`) qui bloquent un jour entier ;
 *  - créneaux TOUJOURS triés par heure de début (midi avant soir) ;
 *  - l'affichage des horaires (Infos & footer) dérive de la MÊME source.
 *
 * `now` est injectable pour des tests déterministes.
 */

/** Un jour de l'éditeur admin : nom FR, ouvert ou non, services [["o","c"], ...]. */
export interface DaySchedule {
  day: string;
  open: boolean;
  slots: [string, string][];
}

/** Horaires indexés par jour JS (0 = dimanche … 6 = samedi). */
export type HoursMap = Record<number, [string, string][]>;

/** Une fermeture exceptionnelle : soit une string "YYYY-MM-DD", soit { date }. */
export type Closure = string | { date?: string | null };

export interface SlotConfig {
  /** Horaires indexés par jour JS (issus de hoursArrayToMap). */
  hours: HoursMap;
  /** Pas entre 2 créneaux, en minutes (défaut 30). */
  stepMin?: number;
  /** Délai de préparation avant le 1ᵉʳ créneau aujourd'hui (défaut 15). */
  prepMin?: number;
  /** Capacité par créneau (câblée ; le comptage réel des commandes viendra plus tard). */
  capacity?: number | null;
  /** Fermetures exceptionnelles (jours bloqués). */
  closures?: string[];
  /** Nombre de jours proposés à l'avance (défaut 3). */
  lookaheadDays?: number;
  /** Instant de référence (injecté pour les tests). */
  now?: Date;
  /**
   * Nb de commandes déjà prises par créneau, clé = `${dayKey}T${label}`.
   * Si fourni avec `capacity`, marque les créneaux pleins comme `taken`.
   * Optionnel → tous les créneaux restent disponibles (comportement legacy actuel).
   */
  booked?: Record<string, number>;
}

export interface TimeSlot {
  /** Timestamp (ms) du créneau — identifiant stable. */
  id: number;
  /** Heure affichée "HH:MM". */
  label: string;
  /** Libellé du jour ("Aujourd'hui", "Demain", "lundi 09 juin"…). */
  dayLabel: string;
  /** Clé du jour "YYYY-MM-DD" (timezone locale). */
  dayKey: string;
  /** Décalage en jours par rapport à aujourd'hui (0 = aujourd'hui). */
  dayOffset: number;
  /** Créneau complet (capacité atteinte). */
  taken: boolean;
}

export const DEFAULT_STEP_MIN = 30;
export const DEFAULT_PREP_MIN = 15;
export const DEFAULT_LOOKAHEAD_DAYS = 3;

/**
 * Clé texte stable d'un créneau choisi : `${dayKey}T${label}` (ex. `2026-06-25T18:30`).
 * SOURCE UNIQUE du format consommé par `parseSlot` (checkout.ts) → pickup_date / pickup_time,
 * et par la clé de capacité `booked`. Depuis la refonte du parcours (panier → page créneau
 * dédiée → checkout, 2026-06-24) la clé est fabriquée ici et non plus en dur dans un composant,
 * pour que le contrat reste verrouillé par un test.
 */
export function slotKey(slot: Pick<TimeSlot, 'dayKey' | 'label'>): string {
  return `${slot.dayKey}T${slot.label}`;
}

const DAY_FR_TO_IDX: Record<string, number> = {
  dimanche: 0,
  lundi: 1,
  mardi: 2,
  mercredi: 3,
  jeudi: 4,
  vendredi: 5,
  samedi: 6,
};

const IDX_TO_DAY_FR = [
  'Dimanche',
  'Lundi',
  'Mardi',
  'Mercredi',
  'Jeudi',
  'Vendredi',
  'Samedi',
];
/** Ordre d'affichage humain : lundi → dimanche. */
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** Tri stable des services par heure de début (M14 : midi avant soir). */
function sortSlots(slots: [string, string][]): [string, string][] {
  return slots
    .filter((s) => Array.isArray(s) && s.length === 2)
    .slice()
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])));
}

/**
 * Convertit le format admin (tableau 7 jours, noms FR, lundi→dimanche) vers la
 * map indexée par jour JS (0=dimanche..6=samedi). Tri défensif des services
 * (corrige des données déjà en base sans re-save — M14).
 * Renvoie null si l'entrée n'est pas un tableau.
 */
export function hoursArrayToMap(arr: unknown): HoursMap | null {
  if (!Array.isArray(arr)) return null;
  const map: HoursMap = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
  arr.forEach((d) => {
    const day = d as Partial<DaySchedule>;
    const idx = DAY_FR_TO_IDX[String(day?.day ?? '').trim().toLowerCase()];
    if (idx === undefined) return;
    map[idx] =
      day?.open === false ? [] : sortSlots(Array.isArray(day?.slots) ? day.slots : []);
  });
  return map;
}

/**
 * Horaires AFFICHÉS (Infos & footer), dérivés de la même map que les créneaux.
 * → l'affichage est toujours identique aux créneaux. Ordre lundi→dimanche.
 * Renvoie [[jour, "11:30 — 14:00 · 18:00 — 02:00"] | [jour, "Fermé"], ...].
 */
export function displayHours(map: HoursMap | null): [string, string][] {
  return DISPLAY_ORDER.map((i) => {
    const services = sortSlots((map && map[i]) || []);
    if (!services.length) return [IDX_TO_DAY_FR[i], 'Fermé'];
    return [IDX_TO_DAY_FR[i], services.map((s) => `${s[0]} — ${s[1]}`).join(' · ')];
  });
}

/** Normalise une liste de fermetures (string | {date}) en string[] "YYYY-MM-DD". */
export function normalizeClosures(closures: Closure[] | null | undefined): string[] {
  if (!Array.isArray(closures)) return [];
  return closures
    .map((c) => (typeof c === 'string' ? c : c?.date ?? ''))
    .filter((s): s is string => Boolean(s));
}

/** Construit une clé YYYY-MM-DD en timezone LOCALE (jamais UTC). */
export function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Génère les créneaux disponibles sur `lookaheadDays` jours.
 * Pur : dépend uniquement de la config (et de `now`, injectable).
 */
export function generateSlots(config: SlotConfig): TimeSlot[] {
  const hours = config.hours || {};
  const stepMin = config.stepMin ?? DEFAULT_STEP_MIN;
  const prepMin = config.prepMin ?? DEFAULT_PREP_MIN;
  const lookahead = config.lookaheadDays ?? DEFAULT_LOOKAHEAD_DAYS;
  const closures = normalizeClosures(config.closures);
  const capacity = config.capacity ?? null;
  const booked = config.booked ?? {};
  const now = config.now ?? new Date();

  const out: TimeSlot[] = [];
  const stepMs = stepMin * 60 * 1000;
  // Heure mini de retrait AUJOURD'HUI : now + temps de prép.
  const minPickupToday = new Date(now.getTime() + prepMin * 60 * 1000);

  for (let dayOffset = 0; dayOffset <= lookahead; dayOffset++) {
    const date = new Date(now);
    date.setDate(date.getDate() + dayOffset);
    date.setHours(0, 0, 0, 0);
    const dayOfWeek = date.getDay();
    const services = sortSlots(hours[dayOfWeek] || []);
    if (services.length === 0) continue; // resto fermé ce jour-là

    const dayKey = localDateKey(date);
    // Fermeture exceptionnelle → on saute le jour entier.
    if (closures.includes(dayKey)) continue;

    const dayLabel =
      dayOffset === 0
        ? "Aujourd'hui"
        : dayOffset === 1
          ? 'Demain'
          : date.toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: '2-digit',
              month: 'short',
            });

    services.forEach(([openStr, closeStr]) => {
      const [oH, oM] = openStr.split(':').map(Number);
      const [cH, cM] = closeStr.split(':').map(Number);
      const open = new Date(date);
      open.setHours(oH, oM, 0, 0);
      const close = new Date(date);
      close.setHours(cH, cM, 0, 0);
      // Fermeture après minuit (ex. 02:00) : la fin est le LENDEMAIN.
      if (close.getTime() <= open.getTime()) close.setDate(close.getDate() + 1);

      // Aujourd'hui : si le service est déjà terminé → skip.
      if (dayOffset === 0 && close.getTime() <= minPickupToday.getTime()) return;

      // Démarre à l'ouverture, ou à (now + prép) si c'est aujourd'hui.
      let startMs = open.getTime();
      if (dayOffset === 0) startMs = Math.max(open.getTime(), minPickupToday.getTime());

      // Arrondit au prochain multiple du pas.
      const t = new Date(startMs);
      const remainder = t.getMinutes() % stepMin;
      if (remainder > 0 || t.getSeconds() > 0) {
        t.setMinutes(t.getMinutes() + (stepMin - remainder), 0, 0);
      } else {
        t.setSeconds(0, 0);
      }

      // Génère jusqu'à un pas avant la fermeture.
      const lastSlot = close.getTime() - stepMs;
      let cur = t.getTime();
      while (cur <= lastSlot) {
        const label = new Date(cur).toTimeString().slice(0, 5);
        const taken =
          capacity != null && capacity > 0
            ? (booked[`${dayKey}T${label}`] ?? 0) >= capacity
            : false;
        out.push({ id: cur, label, dayLabel, dayKey, dayOffset, taken });
        cur += stepMs;
      }
    });

    // Assez de choix → on s'arrête (comme le legacy).
    if (dayOffset === 0 && out.length >= 12) break;
    if (out.length >= 24) break;
  }

  return out;
}

export interface DayStatus {
  type: 'open' | 'closed' | 'passed';
  msg: string | null;
}

/**
 * Statut du jour courant pour le bandeau d'info (port de `todayStatus`).
 *  - closed : fermé aujourd'hui ;
 *  - passed : tous les services du jour sont terminés ;
 *  - open   : commandes possibles aujourd'hui.
 */
export function dayStatus(config: SlotConfig): DayStatus {
  const hours = config.hours || {};
  const prepMin = config.prepMin ?? DEFAULT_PREP_MIN;
  const now = config.now ?? new Date();
  const day = now.getDay();
  const services = sortSlots(hours[day] || []);
  const dayName = now.toLocaleDateString('fr-FR', { weekday: 'long' });

  if (services.length === 0) {
    return { type: 'closed', msg: `Le restaurant est fermé aujourd'hui (${dayName})` };
  }

  const minPickup = new Date(now.getTime() + prepMin * 60 * 1000);
  const allClosed = services.every(([openStr, closeStr]) => {
    const [oh, om] = openStr.split(':').map(Number);
    const [h, m] = closeStr.split(':').map(Number);
    const o = new Date(now);
    o.setHours(oh, om, 0, 0);
    const c = new Date(now);
    c.setHours(h, m, 0, 0);
    if (c.getTime() <= o.getTime()) c.setDate(c.getDate() + 1);
    return c.getTime() <= minPickup.getTime();
  });

  if (allClosed) return { type: 'passed', msg: 'Tous les services sont terminés aujourd’hui' };
  return { type: 'open', msg: null };
}

/** Groupe les créneaux par jour, en préservant l'ordre d'apparition. */
export interface SlotGroup {
  label: string;
  dayKey: string;
  dayOffset: number;
  slots: TimeSlot[];
}

export function groupSlotsByDay(slots: TimeSlot[]): SlotGroup[] {
  const map = new Map<string, SlotGroup>();
  slots.forEach((s) => {
    const key = s.dayKey || 'today';
    if (!map.has(key)) {
      map.set(key, { label: s.dayLabel, dayKey: key, dayOffset: s.dayOffset, slots: [] });
    }
    map.get(key)!.slots.push(s);
  });
  return Array.from(map.values());
}
